import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useGetRequestsSummaryQuery,
  useListRequestsQuery,
  useGetRequestQuery,
  useRetryRequestMutation,
  useCancelRequestMutation,
  useDiscardRequestMutation,
  useListDirectoryQuery,
  useUpdateDirectoryEntryMutation,
  useListTemplatesQuery,
  useUpdateTemplateMutation,
  type NotificationDirectoryEntry,
  type NotificationRequest,
  type NotificationTemplate,
} from '@/redux/internal-services/notificationsAdminApi';
import {
  useGetConfigurationQuery,
  useUpdateConfigurationMutation,
} from '@/redux/internal-services/onboardingApi';

// ── Shared helpers ────────────────────────────────────────────────────────────

const ALL_CHANNELS = ['email', 'sms', 'push', 'in_app'] as const;
const CLASSIFICATIONS = ['Critical', 'Important', 'Info'] as const;

function ChannelBadge({ ch, blue }: { ch: string; blue?: boolean }) {
  const base = 'inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium border';
  const color = blue
    ? 'bg-blue-50 text-blue-700 border-blue-200'
    : 'bg-ui-subtle text-ui-text2 border-ui-border';
  return <span className={`${base} ${color}`}>{ch}</span>;
}

function ChannelCheckboxGroup({
  label,
  selected,
  onChange,
  disabledItems,
}: {
  label: string;
  selected: string[];
  onChange: (v: string[]) => void;
  disabledItems?: string[];
}) {
  const toggle = (ch: string) => {
    if (disabledItems?.includes(ch)) return;
    onChange(selected.includes(ch) ? selected.filter((x) => x !== ch) : [...selected, ch]);
  };
  return (
    <div>
      {label && <p className="text-xs font-medium text-ui-text mb-1">{label}</p>}
      <div className="flex flex-wrap gap-2">
        {ALL_CHANNELS.map((ch) => {
          const isOn = selected.includes(ch);
          const disabled = disabledItems?.includes(ch);
          return (
            <button
              key={ch}
              type="button"
              onClick={() => toggle(ch)}
              disabled={disabled}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                isOn
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-ui-card text-ui-text2 border-gray-300 hover:border-gray-400'
              } ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              {ch}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Status badge ──────────────────────────────────────────────────────────────
// Keys are the raw lowercase values RequestStatus/DeliveryStatus store in Mongo —
// unlike Classification/ActionStatus, these enums are never Title-Case.
const STATUS_STYLES: Record<string, { label: string; className: string }> = {
  requested: { label: 'Requested', className: 'bg-ui-subtle text-ui-text2 border-ui-border' },
  pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  queued: { label: 'Queued', className: 'bg-blue-100 text-blue-700 border-blue-200' },
  scheduled: { label: 'Scheduled', className: 'bg-blue-100 text-blue-700 border-blue-200' },
  sending: { label: 'Sending', className: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  dispatching: { label: 'Dispatching', className: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  sent: { label: 'Delivered', className: 'bg-green-100 text-green-700 border-green-200' },
  partial: { label: 'Partial', className: 'bg-amber-100 text-amber-700 border-amber-200' },
  retried: { label: 'Retried', className: 'bg-amber-100 text-amber-700 border-amber-200' },
  failed: { label: 'Failed', className: 'bg-red-100 text-red-700 border-red-200' },
  cancelled: { label: 'Cancelled', className: 'bg-ui-subtle text-ui-text2 border-ui-border' },
  expired: { label: 'Expired', className: 'bg-ui-subtle text-ui-text2 border-ui-border' },
};

function StatusBadge({ status }: { status: string }) {
  const style = STATUS_STYLES[status] ?? { label: status || '—', className: 'bg-ui-subtle text-ui-text2 border-ui-border' };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium border ${style.className}`}>
      {style.label}
    </span>
  );
}

// ── Requests Tab ──────────────────────────────────────────────────────────────

function RequestsTab() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [skip, setSkip] = useState(0);
  const limit = 25;

  const { data: summary } = useGetRequestsSummaryQuery();
  const { data, isFetching } = useListRequestsQuery({ status: statusFilter || undefined, limit, skip });
  const { data: detail, isFetching: isDetailFetching } = useGetRequestQuery(selectedId!, { skip: !selectedId });

  const [retry] = useRetryRequestMutation();
  const [cancel] = useCancelRequestMutation();
  const [discard] = useDiscardRequestMutation();

  const requests = data?.data ?? [];
  const total = data?.total ?? 0;

  const counts = summary?.data;

  return (
    <div className="flex flex-col gap-4">
      {/* Summary cards */}
      {counts && (
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: 'Total', value: counts.total, color: 'text-ui-text' },
            { label: 'Delivered', value: counts.delivered, color: 'text-green-700' },
            { label: 'Dispatch Failed', value: counts.dispatch_failed, color: 'text-red-700' },
            { label: 'Queue Failed', value: counts.queue_failed, color: 'text-amber-700' },
          ].map(({ label, value, color }) => (
            <div key={label} className="rounded-lg border border-ui-border bg-ui-card px-4 py-3">
              <p className="text-xs text-ui-text2">{label}</p>
              <p className={`text-2xl font-semibold ${color}`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3">
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setSkip(0); }}
          className="rounded border border-gray-300 bg-ui-card px-3 py-1.5 text-sm text-ui-text focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All statuses</option>
          {[
            { value: 'requested', label: 'Requested' },
            { value: 'queued', label: 'Queued' },
            { value: 'scheduled', label: 'Scheduled' },
            { value: 'sent', label: 'Delivered' },
            { value: 'partial', label: 'Partial' },
            { value: 'failed', label: 'Failed' },
            { value: 'cancelled', label: 'Cancelled' },
            { value: 'expired', label: 'Expired' },
          ].map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        <span className="text-xs text-ui-text3">{total} results</span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-ui-border bg-ui-card">
        <table className="min-w-full text-sm">
          <thead className="bg-ui-raised text-xs text-ui-text2 uppercase tracking-wide">
            <tr>
              <th className="px-4 py-3 text-left">Code</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Classification</th>
              <th className="px-4 py-3 text-left">Channels</th>
              <th className="px-4 py-3 text-left">Recipient</th>
              <th className="px-4 py-3 text-left">Created</th>
              <th className="px-4 py-3 text-left">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ui-border2">
            {isFetching && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-sm text-ui-text3">Loading...</td></tr>
            )}
            {!isFetching && requests.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-sm text-ui-text3">No requests found.</td></tr>
            )}
            {!isFetching && requests.map((r: NotificationRequest) => (
              <React.Fragment key={r.id}>
                <tr className={`hover:bg-ui-hover ${r.id === selectedId ? 'bg-ui-hover' : ''}`}>
                  <td className="px-4 py-3 font-mono text-xs text-ui-text">{r.notification_code}</td>
                  <td className="px-4 py-3"><StatusBadge status={r.status ?? ''} /></td>
                  <td className="px-4 py-3 text-xs text-ui-text2">{r.classification}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(r.resolved_channels ?? []).map((ch) => <ChannelBadge key={ch} ch={ch} />)}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-ui-text2">
                    {r.recipient?.to_email ?? r.recipient?.to_phone ?? r.recipient?.user_id ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-ui-text3">
                    {r.created_on ? new Date(r.created_on).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setSelectedId(r.id === selectedId ? null : r.id)}
                        className={`text-xs hover:underline ${r.id === selectedId ? 'text-blue-800 font-semibold' : 'text-blue-600'}`}
                      >
                        {r.id === selectedId ? 'Hide' : 'Detail'}
                      </button>
                      {(r.status === 'failed' || r.queue_status === 'failed') && (
                        <button
                          onClick={() => retry(r.id)}
                          className="text-xs text-green-600 hover:underline"
                        >
                          Retry
                        </button>
                      )}
                      {(r.status === 'queued' || r.status === 'scheduled') && (
                        <button
                          onClick={() => cancel(r.id)}
                          className="text-xs text-amber-600 hover:underline"
                        >
                          Cancel
                        </button>
                      )}
                      <button
                        onClick={() => discard(r.id)}
                        className="text-xs text-red-500 hover:underline"
                      >
                        Discard
                      </button>
                    </div>
                  </td>
                </tr>
                {r.id === selectedId && (
                  <tr className="bg-ui-raised">
                    <td colSpan={7} className="px-6 py-4 border-b border-ui-border">
                      {isDetailFetching || !detail?.data ? (
                        <p className="text-xs text-ui-text3">Loading…</p>
                      ) : (
                        <div className="flex flex-col gap-3">
                          <div className="grid grid-cols-3 gap-3 text-xs text-ui-text2">
                            <div><span className="font-medium">ID:</span> {detail.data.id}</div>
                            <div><span className="font-medium">Code:</span> {detail.data.notification_code}</div>
                            <div><span className="font-medium">Status:</span> {detail.data.status}</div>
                            <div><span className="font-medium">Delivery mode:</span> {detail.data.delivery_mode}</div>
                            <div><span className="font-medium">Attempts:</span> {detail.data.attempts_count}</div>
                            <div><span className="font-medium">Sent:</span> {detail.data.sent_on ? new Date(detail.data.sent_on).toLocaleString() : '—'}</div>
                          </div>
                          {detail.data.last_error && (
                            <p className="rounded bg-red-50 px-3 py-2 text-xs text-red-700 font-mono">{detail.data.last_error}</p>
                          )}
                          {(detail.data.delivery_logs ?? []).length > 0 && (
                            <div>
                              <p className="text-xs font-medium text-ui-text mb-2">Delivery Logs</p>
                              <div className="space-y-1">
                                {detail.data.delivery_logs!.map((log) => (
                                  <div key={log.id} className="flex items-center gap-3 rounded bg-ui-card px-3 py-1.5 text-xs border border-ui-border">
                                    <ChannelBadge ch={log.channel ?? ''} />
                                    <StatusBadge status={log.delivery_status ?? ''} />
                                    <span className="text-ui-text2">{log.recipient_address}</span>
                                    {log.failure_reason && <span className="text-red-500">{log.failure_reason}</span>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-between text-xs text-ui-text2">
          <span>Showing {skip + 1}–{Math.min(skip + limit, total)} of {total}</span>
          <div className="flex gap-2">
            <button
              disabled={skip === 0}
              onClick={() => setSkip(Math.max(0, skip - limit))}
              className="rounded border px-3 py-1 disabled:opacity-40"
            >
              Prev
            </button>
            <button
              disabled={skip + limit >= total}
              onClick={() => setSkip(skip + limit)}
              className="rounded border px-3 py-1 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

// ── Templates Tab ─────────────────────────────────────────────────────────────

function TemplatesTab() {
  const [codeFilter, setCodeFilter] = useState('');
  const [channelFilter, setChannelFilter] = useState('');
  const [editing, setEditing] = useState<NotificationTemplate | null>(null);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [editActive, setEditActive] = useState(true);
  const [saveOk, setSaveOk] = useState(false);

  const { data, isFetching } = useListTemplatesQuery({
    notification_code: codeFilter || undefined,
    channel: channelFilter || undefined,
  });

  const [updateTemplate] = useUpdateTemplateMutation();

  const templates = data?.data ?? [];

  const openEdit = (t: NotificationTemplate) => {
    setSaveOk(false);
    setEditing(t);
    setEditSubject(t.subject_template ?? '');
    setEditBody(t.body_template ?? '');
    setEditActive(t.is_active);
  };

  const closeEdit = () => setEditing(null);

  const handleSave = async () => {
    if (!editing) return;
    const body: Record<string, unknown> = {};
    if (editSubject !== editing.subject_template) body.subject_template = editSubject;
    if (editBody !== editing.body_template) body.body_template = editBody;
    if (editActive !== editing.is_active) body.is_active = editActive;
    if (Object.keys(body).length === 0) { closeEdit(); return; }
    try {
      await updateTemplate({ id: editing.id, body }).unwrap();
      setSaveOk(true);
      setTimeout(closeEdit, 900);
    } catch {}
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <input
          type="text"
          placeholder="Filter by notification code..."
          value={codeFilter}
          onChange={(e) => setCodeFilter(e.target.value)}
          className="rounded border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <select
          value={channelFilter}
          onChange={(e) => setChannelFilter(e.target.value)}
          className="rounded border border-gray-300 bg-ui-card px-3 py-1.5 text-sm text-ui-text focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All channels</option>
          {ALL_CHANNELS.map((ch) => <option key={ch} value={ch}>{ch}</option>)}
        </select>
      </div>

      <div className="overflow-x-auto rounded-lg border border-ui-border bg-ui-card">
        <table className="min-w-full text-sm">
          <thead className="bg-ui-raised text-xs text-ui-text2 uppercase tracking-wide">
            <tr>
              <th className="px-4 py-3 text-left">Code</th>
              <th className="px-4 py-3 text-left">Channel</th>
              <th className="px-4 py-3 text-left">Language</th>
              <th className="px-4 py-3 text-left">Version</th>
              <th className="px-4 py-3 text-left">Active</th>
              <th className="px-4 py-3 text-left">Tenant</th>
              <th className="px-4 py-3 text-left">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ui-border2">
            {isFetching && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-sm text-ui-text3">Loading...</td></tr>
            )}
            {!isFetching && templates.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-sm text-ui-text3">No templates found.</td></tr>
            )}
            {!isFetching && templates.map((t: NotificationTemplate) => (
              <tr key={t.id} className="hover:bg-ui-hover">
                <td className="px-4 py-3 font-mono text-xs text-ui-text">{t.notification_code}</td>
                <td className="px-4 py-3"><ChannelBadge ch={t.channel ?? ''} blue /></td>
                <td className="px-4 py-3 text-xs text-ui-text2">{t.language}</td>
                <td className="px-4 py-3 text-xs text-ui-text2">v{t.version}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium border ${
                    t.is_active ? 'bg-green-50 text-green-700 border-green-200' : 'bg-ui-subtle text-ui-text2 border-ui-border'
                  }`}>
                    {t.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-ui-text3">{t.tenant_id ?? 'System'}</td>
                <td className="px-4 py-3">
                  <button onClick={() => openEdit(t)} className="text-xs text-blue-600 hover:underline">Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit drawer */}
      {editing && (
        <div className="fixed inset-0 z-40 flex justify-end">
          <div className="absolute inset-0 bg-black/20" onClick={closeEdit} />
          <div className="relative z-50 flex flex-col bg-ui-card w-full max-w-lg shadow-xl overflow-y-auto">
            <div className="flex items-center justify-between border-b border-ui-border px-5 py-4">
              <div>
                <p className="font-mono text-xs text-ui-text2">{editing.notification_code}</p>
                <p className="text-sm font-semibold text-ui-text">Edit Template</p>
              </div>
              <button onClick={closeEdit} className="text-ui-text3 hover:text-ui-text2">
                <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                </svg>
              </button>
            </div>
            <div className="flex flex-col gap-5 px-5 py-4 flex-1">
              <div className="flex gap-3 text-xs text-ui-text2">
                <ChannelBadge ch={editing.channel ?? ''} blue />
                <span>Lang: {editing.language}</span>
                <span>v{editing.version}</span>
              </div>

              <div>
                <label className="block text-xs font-medium text-ui-text mb-1">Subject Template</label>
                <input
                  type="text"
                  value={editSubject}
                  onChange={(e) => setEditSubject(e.target.value)}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-ui-text mb-1">Body Template</label>
                <textarea
                  value={editBody}
                  onChange={(e) => setEditBody(e.target.value)}
                  rows={8}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex items-center justify-between rounded-lg border border-ui-border bg-ui-raised px-4 py-3">
                <div>
                  <p className="text-xs font-medium text-ui-text">Active</p>
                  <p className="text-xs text-ui-text3">Inactive templates are not used for dispatch.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setEditActive(!editActive)}
                  className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${editActive ? 'bg-blue-600' : 'bg-gray-300'}`}
                >
                  <span className={`inline-block h-4 w-4 rounded-full bg-ui-card shadow transition-transform ${editActive ? 'translate-x-4' : 'translate-x-0'}`} />
                </button>
              </div>

              {editing.required_placeholders.length > 0 && (
                <div className="rounded-lg border border-ui-border bg-ui-raised px-4 py-3">
                  <p className="text-xs font-medium text-ui-text mb-1">Required Placeholders</p>
                  <div className="flex flex-wrap gap-1">
                    {editing.required_placeholders.map((p) => (
                      <span key={p} className="font-mono text-xs bg-ui-card border border-ui-border rounded px-1.5 py-0.5 text-ui-text2">{`{{${p}}}`}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="border-t border-ui-border px-5 py-4 flex gap-3">
              <button
                onClick={handleSave}
                className={`flex-1 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${saveOk ? 'bg-green-600 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
              >
                {saveOk ? 'Saved!' : 'Save'}
              </button>
              <button onClick={closeEdit} className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm text-ui-text hover:bg-ui-hover">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Directory Tab ─────────────────────────────────────────────────────────────

function DirectoryTab() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [channelFilter, setChannelFilter] = useState('');
  const [classFilter, setClassFilter] = useState('');

  const [editing, setEditing] = useState<NotificationDirectoryEntry | null>(null);
  const [editClassification, setEditClassification] = useState('');
  const [editMandatory, setEditMandatory] = useState<string[]>([]);
  const [, setEditOptional] = useState<string[]>([]);
  const [editTenantOverride, setEditTenantOverride] = useState(true);
  const [editIsActive, setEditIsActive] = useState(true);
  const [saveOk, setSaveOk] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  const { data, isFetching } = useListDirectoryQuery({
    search: search || undefined,
    channel: channelFilter || undefined,
    classification: classFilter || undefined,
  });

  const [updateEntry] = useUpdateDirectoryEntryMutation();

  const entries = data?.data ?? [];

  const openEdit = (e: NotificationDirectoryEntry) => {
    setSaveOk(false);
    setShowGuide(false);
    setEditing(e);
    setEditClassification(e.default_classification ?? e.classification ?? '');
    setEditMandatory(e.mandatory_channels ?? []);
    setEditOptional((e.optional_channels ?? []).filter((c) => !(e.mandatory_channels ?? []).includes(c)));
    setEditTenantOverride(e.tenant_override_allowed ?? true);
    setEditIsActive(e.is_active);
  };

  const closeEdit = () => setEditing(null);

  const handleSave = async () => {
    if (!editing) return;
    const body: {
      default_classification?: string | null;
      tenant_override_allowed?: boolean;
      mandatory_channels?: string[];
      is_active?: boolean;
    } = {};

    const origClassification = editing.default_classification ?? editing.classification ?? '';
    if (editClassification !== origClassification) body.default_classification = editClassification || null;

    if (editTenantOverride !== (editing.tenant_override_allowed ?? true)) body.tenant_override_allowed = editTenantOverride;

    if (!editTenantOverride) {
      const origMandatory = JSON.stringify([...(editing.mandatory_channels ?? [])].sort());
      if (JSON.stringify([...editMandatory].sort()) !== origMandatory) body.mandatory_channels = editMandatory;
    }

    if (editIsActive !== editing.is_active) body.is_active = editIsActive;

    if (Object.keys(body).length === 0) { closeEdit(); return; }

    try {
      await updateEntry({ notification_code: editing.notification_code!, body }).unwrap();
      setSaveOk(true);
      setTimeout(closeEdit, 900);
    } catch {}
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <input
          type="text"
          placeholder="Search code or name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <select
          value={channelFilter}
          onChange={(e) => setChannelFilter(e.target.value)}
          className="rounded border border-gray-300 bg-ui-card px-3 py-1.5 text-sm text-ui-text focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All channels</option>
          {ALL_CHANNELS.map((ch) => <option key={ch} value={ch}>{ch}</option>)}
        </select>
        <select
          value={classFilter}
          onChange={(e) => setClassFilter(e.target.value)}
          className="rounded border border-gray-300 bg-ui-card px-3 py-1.5 text-sm text-ui-text focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All classifications</option>
          {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <span className="text-xs text-ui-text3">{entries.length} entries</span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-ui-border bg-ui-card">
        <table className="min-w-full text-sm">
          <thead className="bg-ui-raised text-xs text-ui-text2 uppercase tracking-wide">
            <tr>
              <th className="px-4 py-3 text-left">Code</th>
              <th className="px-4 py-3 text-left">Classification</th>
              <th className="px-4 py-3 text-left">Mandatory Channels</th>
              <th className="px-4 py-3 text-left">Optional Channels</th>
              <th className="px-4 py-3 text-center">System</th>
              <th className="px-4 py-3 text-left">Active</th>
              <th className="px-4 py-3 text-left">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ui-border2">
            {isFetching && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-sm text-ui-text3">Loading...</td></tr>
            )}
            {!isFetching && entries.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-6 text-center text-sm text-ui-text3">No entries found.</td></tr>
            )}
            {!isFetching && entries.map((e: NotificationDirectoryEntry) => {
              const classification = e.default_classification ?? e.classification;
              const classColor =
                classification === 'Critical'
                  ? 'bg-red-100 text-red-700 border border-red-200'
                  : classification === 'Important'
                  ? 'bg-amber-100 text-amber-800 border border-amber-200'
                  : classification === 'Info'
                  ? 'bg-sky-100 text-sky-700 border border-sky-200'
                  : 'bg-ui-subtle text-ui-text2';

              return (
                <tr key={e.id} className="hover:bg-ui-hover">
                  <td className="px-4 py-3 font-mono text-xs text-ui-text">{e.id}</td>
                  <td className="px-4 py-3">
                    {classification ? (
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${classColor}`}>
                        {classification}
                      </span>
                    ) : (
                      <span className="text-ui-text3 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(e.mandatory_channels ?? []).map((ch) => <ChannelBadge key={ch} ch={ch} />)}
                      {(e.mandatory_channels ?? []).length === 0 && <span className="text-ui-text3 text-xs">—</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(e.optional_channels ?? []).map((ch) => <ChannelBadge key={ch} ch={ch} blue />)}
                      {(e.optional_channels ?? []).length === 0 && <span className="text-ui-text3 text-xs">—</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {e.is_system_critical ? (
                      <svg className="w-4 h-4 text-ui-text3 mx-auto" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd"/>
                      </svg>
                    ) : (
                      <span className="text-ui-text3 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium border ${
                      e.is_active ? 'bg-green-50 text-green-700 border-green-200' : 'bg-ui-subtle text-ui-text2 border-ui-border'
                    }`}>
                      {e.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {e.is_system_critical ? (
                      <span className="text-xs text-ui-text3">Locked</span>
                    ) : (
                      <button onClick={() => openEdit(e)} className="text-xs text-blue-600 hover:underline">Edit</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Edit drawer */}
      {editing && (
        <div className="fixed inset-0 z-40 flex justify-end">
          <div className="absolute inset-0 bg-black/20" onClick={closeEdit} />
          <div className="relative z-50 flex flex-col bg-ui-card w-full max-w-lg shadow-xl overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-ui-border px-5 py-4">
              <div>
                <p className="font-mono text-xs text-ui-text2">{editing.notification_code}</p>
                <p className="text-sm font-semibold text-ui-text">Edit Directory Entry</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setShowGuide(!showGuide)}
                  className="text-xs text-amber-600 hover:text-amber-700 font-medium"
                >
                  ? How this works
                </button>
                <button onClick={closeEdit} className="text-ui-text3 hover:text-ui-text2">
                  <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
                  </svg>
                </button>
              </div>
            </div>

            <div className="flex flex-col gap-5 px-5 py-4 flex-1">
              {/* Guide panel */}
              {showGuide && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800 space-y-2">
                  <p className="font-semibold text-amber-900">How notification dispatch works</p>
                  <ol className="list-decimal list-inside space-y-1 leading-relaxed">
                    <li><span className="font-medium">Classification</span> — each directory entry has a default classification (Critical / Important / Info) that determines urgency.</li>
                    <li><span className="font-medium">Channel matrix</span> — the system maps each classification to a set of channels (e.g. Critical fires email + SMS + push). This matrix lives in the <span className="font-mono bg-amber-100 rounded px-1">notification</span> configuration catalogue.</li>
                    <li><span className="font-medium">Tenant override</span> — when enabled, a tenant can replace the system matrix with their own. When disabled, the system matrix is enforced and mandatory channels always fire regardless of tenant or user preferences.</li>
                    <li><span className="font-medium">Mandatory channels</span> — only relevant when tenant override is OFF. These channels fire unconditionally, even during quiet hours.</li>
                  </ol>
                  <p className="text-amber-700 leading-relaxed">At send time: directory entry classification &rarr; resolve channel matrix (system or tenant) &rarr; intersect with user preferences &rarr; apply quiet hours &rarr; dispatch.</p>
                </div>
              )}

              {/* Description */}
              {editing.description && (
                <p className="text-xs italic text-ui-text3">{editing.description}</p>
              )}

              {/* Classification */}
              <div>
                <label className="block text-xs font-medium text-ui-text mb-1">Classification</label>
                <select
                  value={editClassification}
                  onChange={(e) => setEditClassification(e.target.value)}
                  className="w-full rounded border border-gray-300 bg-ui-card px-3 py-2 text-sm text-ui-text focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">None</option>
                  {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              {/* Tenant Override toggle */}
              <div className="rounded-lg border border-ui-border bg-ui-raised px-4 py-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-ui-text">Tenant Override</p>
                    <p className="text-xs text-ui-text3 mt-0.5">
                      {editTenantOverride
                        ? 'Tenants can configure their own channel matrix in Settings. System defaults are advisory.'
                        : 'System matrix is enforced. Tenant settings are ignored; mandatory channels always fire.'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setEditTenantOverride(!editTenantOverride)}
                    className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${editTenantOverride ? 'bg-blue-600' : 'bg-gray-300'}`}
                  >
                    <span className={`inline-block h-4 w-4 rounded-full bg-ui-card shadow transition-transform ${editTenantOverride ? 'translate-x-4' : 'translate-x-0'}`} />
                  </button>
                </div>
              </div>

              {/* Channel matrix info box */}
              <div className="rounded-lg border border-ui-border bg-ui-raised px-4 py-3 space-y-2">
                <div className="flex items-start gap-3">
                  <svg className="w-4 h-4 text-ui-text3 shrink-0 mt-0.5" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm.75 10.5h-1.5v-5h1.5v5zm0-6.5h-1.5V3.5h1.5V5z"/>
                  </svg>
                  <div>
                    <p className="text-xs font-medium text-ui-text">Channel Matrix</p>
                    <p className="text-xs text-ui-text3 mt-0.5 leading-relaxed">
                      The active channel matrix (which channels fire per classification) is stored in the{' '}
                      <span className="font-medium text-ui-text2">notification</span> configuration catalogue — not per directory entry. The system matrix is the platform default; tenants override it in Settings &rarr; Notifications.
                    </p>
                    <button
                      type="button"
                      onClick={() => navigate('/platform/configurations?prefix=notification')}
                      className="mt-2 text-xs text-blue-600 hover:underline"
                    >
                      Open in Configurations &rarr;
                    </button>
                  </div>
                </div>
              </div>

              {/* Mandatory channels — only when tenant override is OFF */}
              {!editTenantOverride && (
                <div>
                  <p className="text-xs font-medium text-ui-text mb-0.5">Mandatory Channels</p>
                  <p className="text-xs text-ui-text3 mb-2">Always fire — cannot be suppressed by any tenant or user setting.</p>
                  <ChannelCheckboxGroup
                    label=""
                    selected={editMandatory}
                    onChange={setEditMandatory}
                  />
                </div>
              )}

              {/* Active toggle */}
              <div className="rounded-lg border border-ui-border bg-ui-raised px-4 py-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-ui-text">Active</p>
                    <p className="text-xs text-ui-text3 mt-0.5">Inactive entries are ignored by the notification engine.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setEditIsActive(!editIsActive)}
                    className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${editIsActive ? 'bg-blue-600' : 'bg-gray-300'}`}
                  >
                    <span className={`inline-block h-4 w-4 rounded-full bg-ui-card shadow transition-transform ${editIsActive ? 'translate-x-4' : 'translate-x-0'}`} />
                  </button>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-ui-border px-5 py-4 flex gap-3">
              <button
                onClick={handleSave}
                className={`flex-1 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${saveOk ? 'bg-green-600 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
              >
                {saveOk ? 'Saved!' : 'Save'}
              </button>
              <button onClick={closeEdit} className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm text-ui-text hover:bg-ui-hover">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Preferences Tab ───────────────────────────────────────────────────────────

const CHANNEL_LABELS: Record<string, string> = { in_app: 'In-App', push: 'Push', email: 'Email', sms: 'SMS' };

function PreferencesTab() {
  const { data: cfgData, isLoading } = useGetConfigurationQuery('notification');
  const [updateConfig, { isLoading: isSaving }] = useUpdateConfigurationMutation();

  const cfg = (cfgData?.data?.effective_value ?? {}) as Record<string, unknown>;
  const matrix = (cfg.matrix ?? {}) as Record<string, Record<string, boolean>>;
  const quietHours = (cfg.quiet_hours ?? { enabled: false, from: '22:00', to: '07:00' }) as { enabled: boolean; from: string; to: string };
  const timezone = (cfg.timezone ?? 'UTC') as string;

  const [localMatrix, setLocalMatrix] = useState<Record<string, Record<string, boolean>> | null>(null);
  const [localQH, setLocalQH] = useState<{ enabled: boolean; from: string; to: string } | null>(null);
  const [localTz, setLocalTz] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const activeMatrix = localMatrix ?? matrix;
  const activeQH = localQH ?? quietHours;
  const activeTz = localTz ?? timezone;

  const toggleChannel = (cls: string, ch: string) => {
    const current = activeMatrix[cls] ?? {};
    setLocalMatrix({ ...activeMatrix, [cls]: { ...current, [ch]: !current[ch] } });
    setSaved(false);
  };

  const handleSave = async () => {
    await updateConfig({
      key: 'notification',
      value: { ...cfg, matrix: activeMatrix, quiet_hours: activeQH, timezone: activeTz },
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  if (isLoading) {
    return <div className="py-12 text-center text-sm text-ui-text3">Loading...</div>;
  }

  return (
    <div className="flex flex-col gap-6 max-w-3xl">
      {/* Channel delivery matrix */}
      <div className="rounded-lg border border-ui-border bg-ui-card overflow-hidden">
        <div className="px-4 py-3 border-b border-ui-border2 bg-ui-raised">
          <h3 className="text-sm font-semibold text-ui-text">Channel Delivery Matrix</h3>
          <p className="text-xs text-ui-text3 mt-0.5">Platform-wide defaults. Tenants can override per their settings.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-ui-border2">
                <th className="px-4 py-2.5 text-left text-xs text-ui-text2 font-medium w-32">Classification</th>
                {ALL_CHANNELS.map((ch) => (
                  <th key={ch} className="px-4 py-2.5 text-center text-xs text-ui-text2 font-medium">{CHANNEL_LABELS[ch] ?? ch}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {CLASSIFICATIONS.map((cls) => {
                const row = activeMatrix[cls.toLowerCase()] ?? {};
                return (
                  <tr key={cls} className="hover:bg-ui-hover">
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        cls === 'Critical' ? 'bg-red-100 text-red-700' :
                        cls === 'Important' ? 'bg-amber-100 text-amber-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>{cls}</span>
                    </td>
                    {ALL_CHANNELS.map((ch) => (
                      <td key={ch} className="px-4 py-3 text-center">
                        <button
                          type="button"
                          role="switch"
                          aria-checked={!!row[ch]}
                          onClick={() => toggleChannel(cls.toLowerCase(), ch)}
                          className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors focus:outline-none ${row[ch] ? 'bg-brand-dark' : 'bg-gray-200'}`}
                        >
                          <span className={`inline-block h-4 w-4 rounded-full bg-ui-card shadow transform transition-transform mt-0.5 ${row[ch] ? 'translate-x-4' : 'translate-x-0.5'}`} />
                        </button>
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quiet hours */}
      <div className="rounded-lg border border-ui-border bg-ui-card overflow-hidden">
        <div className="px-4 py-3 border-b border-ui-border2 bg-ui-raised">
          <h3 className="text-sm font-semibold text-ui-text">Quiet Hours Default</h3>
          <p className="text-xs text-ui-text3 mt-0.5">Platform default. Tenants and users can override. During quiet hours, only in-app notifications are delivered.</p>
        </div>
        <div className="px-4 py-4 flex flex-wrap items-center gap-6">
          <label className="flex items-center gap-2 text-sm text-ui-text cursor-pointer">
            <button
              type="button"
              role="switch"
              aria-checked={activeQH.enabled}
              onClick={() => { setLocalQH({ ...activeQH, enabled: !activeQH.enabled }); setSaved(false); }}
              className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors focus:outline-none ${activeQH.enabled ? 'bg-brand-dark' : 'bg-gray-200'}`}
            >
              <span className={`inline-block h-4 w-4 rounded-full bg-ui-card shadow transform transition-transform mt-0.5 ${activeQH.enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
            </button>
            Enabled
          </label>
          <label className="flex items-center gap-2 text-sm text-ui-text2">
            From
            <input
              type="time"
              value={activeQH.from}
              onChange={(e) => { setLocalQH({ ...activeQH, from: e.target.value }); setSaved(false); }}
              className="rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-dark/30"
            />
          </label>
          <label className="flex items-center gap-2 text-sm text-ui-text2">
            To
            <input
              type="time"
              value={activeQH.to}
              onChange={(e) => { setLocalQH({ ...activeQH, to: e.target.value }); setSaved(false); }}
              className="rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-brand-dark/30"
            />
          </label>
        </div>
      </div>

      {/* Timezone */}
      <div className="rounded-lg border border-ui-border bg-ui-card overflow-hidden">
        <div className="px-4 py-3 border-b border-ui-border2 bg-ui-raised">
          <h3 className="text-sm font-semibold text-ui-text">Default Timezone</h3>
          <p className="text-xs text-ui-text3 mt-0.5">Platform default for quiet-hours evaluation. Tenants and users can override to their own timezone.</p>
        </div>
        <div className="px-4 py-4">
          <input
            type="text"
            value={activeTz}
            onChange={(e) => { setLocalTz(e.target.value); setSaved(false); }}
            placeholder="e.g. America/New_York"
            className="rounded border border-gray-300 px-3 py-1.5 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-brand-dark/30"
          />
          <p className="text-xs text-ui-text3 mt-1.5">IANA timezone string (e.g. UTC, America/Chicago, Europe/London)</p>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="rounded-lg bg-brand-dark px-4 py-2 text-sm font-medium text-white hover:bg-brand-dark/90 disabled:opacity-50 transition-colors"
        >
          {isSaving ? 'Saving…' : 'Save Changes'}
        </button>
        {saved && <span className="text-sm text-green-600">Saved</span>}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'requests', label: 'Requests' },
  { key: 'directory', label: 'Directory' },
  { key: 'templates', label: 'Templates' },
  { key: 'preferences', label: 'Preferences' },
] as const;

type TabKey = typeof TABS[number]['key'];

export default function NotificationsAdminPage() {
  const [activeTab, setActiveTab] = useState<TabKey>('requests');

  return (
    <div className="flex flex-col min-h-screen bg-ui-raised">
      {/* Page header */}
      <div className="border-b border-ui-border bg-ui-card px-6 py-4">
        <h1 className="text-lg font-semibold text-ui-text">Notification Management</h1>
        <p className="text-xs text-ui-text2 mt-0.5">Monitor requests, manage templates, configure directory entries, and inspect queue health.</p>
      </div>

      {/* Tab bar */}
      <div className="border-b border-ui-border bg-ui-card px-6">
        <nav className="flex gap-1 -mb-px">
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === key
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-ui-text2 hover:text-ui-text hover:border-gray-300'
              }`}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      <div className="flex-1 px-6 py-5">
        {activeTab === 'requests' && <RequestsTab />}
        {activeTab === 'directory' && <DirectoryTab />}
        {activeTab === 'templates' && <TemplatesTab />}
        {activeTab === 'preferences' && <PreferencesTab />}
      </div>
    </div>
  );
}
